/// <reference path="../../wwwroot/Scripts/gojs/release/go.d.ts" />

namespace Matrix.Visualizer {

    export class ContextMenuItemClickEventArgs {
        Node: GraphNodeRow;
        Item: GraphContextMenuItem;
    }

    export class DiagramFilter {
        NodeKey: string;
        ShowAllrelated: boolean;
        ShowParents: ParentsFilter;
        ShowChildren: ChildrenFilter;
        ShowSiblings: boolean;
    }

    export class Diagram extends go.Diagram {
        private $ = go.GraphObject.make;
        private data: GraphDataResponse;
        private filter: DiagramFilter;
        public OnFocusChange: (data: any) => void;

        constructor(id: string, data: GraphDataResponse, filter: DiagramFilter) {
            super(id);

            (go as any).licenseKey = "2bf843e5b46658c511d35a25403e7efb0bab2d67cb854af5595317f2ed587a0425c9bb285184db9086fb4cf51b2ac98bd8c63b2d934e0338e761d4df41b0d6a9b76227b3175d428dac0073c69da979a3f87c71fac7e027a2897889a2edaec69b5abaa1d05ada08ee797e0663067fbd5ab4fad973e602cf506b6c";

            this.initialAutoScale = go.Diagram.UniformToFill;

            this.filter = filter;
            this.layout = this.$(go.LayeredDigraphLayout,
                {
                    direction: 90,
                    layerSpacing: 25,
                    columnSpacing: 5,
                    cycleRemoveOption: go.LayeredDigraphLayout.CycleDepthFirst,
                    initializeOption: go.LayeredDigraphLayout.LayerOptimalLinkLength,
                    aggressiveOption: go.LayeredDigraphLayout.InitDepthFirstOut,
                    packOption: 7,
                    setsPortSpots: true
                });

            this.$(go.Overview, "overviewDiv",  // the HTML DIV element for the Overview
                { observed: this, contentAlignment: go.Spot.Center });   // tell it which Diagram to show and pan

            this.setData(data);
            this.setTemplatesAndSettings(data);

            let { nodes, edges } = this.filterNodes();
            this.model = this.$(go.GraphLinksModel,
                {
                    nodeKeyProperty: GraphNodeRow.Fields.NodeKey,
                    linkKeyProperty: GraphNodeRow.Fields.NodeKey,
                    linkFromKeyProperty: GraphEdgeRow.Fields.ParentNodeKey,
                    linkToKeyProperty: GraphEdgeRow.Fields.ChildNodeKey,
                    nodeDataArray: nodes,
                    linkDataArray: edges
                });

            this.setSelectedNode();
            this.showHideOverview();
            this.toolManager.hoverDelay = 250; //default of 850ms is a bit slow for tooltips
        }

        public refreshData(data: GraphDataResponse, filter: DiagramFilter) {
            this.filter = filter;
            this.setData(data);
            this.setTemplatesAndSettings(data);
            this.focusNode(filter);
        }

        public filterNodes() {

            const data = Q.deepClone(this.data);

            if (this.filter.NodeKey) {

                let nodes: string[] = [];

                // add node itself
                nodes.push(this.filter.NodeKey);

                //get parents
                if (this.filter.ShowParents === ParentsFilter.AllAncestors) {
                    this.filterNodesRecursively(nodes, this.filter.NodeKey, true);
                }

                if (this.filter.ShowParents === ParentsFilter.ImmediateParents) {
                    let immediateParents: string[] = this.data.Edges.filter(x => x[GraphEdgeRow.Fields.ChildNodeKey] === this.filter.NodeKey).map(x => x[GraphEdgeRow.Fields.ParentNodeKey]);
                    nodes = nodes.concat(immediateParents);
                }

                //get children
                if (this.filter.ShowChildren === ChildrenFilter.AllDescendants) {
                    this.filterNodesRecursively(nodes, this.filter.NodeKey, false);
                }

                if (this.filter.ShowChildren === ChildrenFilter.ImmediateChildren) {
                    let immediateChildren: string[] = this.data.Edges.filter(x => x[GraphEdgeRow.Fields.ParentNodeKey] === this.filter.NodeKey).map(x => x[GraphEdgeRow.Fields.ChildNodeKey]);
                    nodes = nodes.concat(immediateChildren);
                }

                // get siblings
                if (this.filter.ShowSiblings) {
                    let immediateParents: string[] = this.data.Edges.filter(x => x[GraphEdgeRow.Fields.ChildNodeKey] === this.filter.NodeKey).map(x => x[GraphEdgeRow.Fields.ParentNodeKey]);

                    immediateParents.forEach((x) => {
                        if (this.filter.NodeKey !== x) {
                            const siblings = this.data.Edges
                                .filter(y => y[GraphEdgeRow.Fields.ParentNodeKey] === x)
                                .map(y => y[GraphEdgeRow.Fields.ChildNodeKey]);

                            nodes = nodes.concat(siblings);
                        }
                    });
                }

                const focusedNodes = data.Nodes.filter(x => nodes.some(y => x[GraphNodeRow.Fields.NodeKey] === y));
                const focusedLinks = data.Edges.filter(x =>
                    nodes.some(y => x[GraphEdgeRow.Fields.ParentNodeKey] === y) &&
                    nodes.some(y => x[GraphEdgeRow.Fields.ChildNodeKey] === y));
                return { nodes: focusedNodes, edges: focusedLinks };
            } else {
                return { nodes: data.Nodes, edges: data.Edges };
            }
        }

        public focusNode(filter: DiagramFilter) {
            this.filter = filter;

            let { nodes, edges } = this.filterNodes();
            this.refreshGraphData(nodes, edges);

            this.setSelectedNode();

            this.showHideOverview();
        }

        public setSelectedNode() {
            if (this.filter.NodeKey) {

                const part = this.findPartForKey(this.filter.NodeKey);
                if (part) {
                    this.centerRect(part.actualBounds);
                    //this.scrollToRect(node.actualBounds);
                }

                this.model.commit((m) => {

                    // This is the safe way to change model data
                    // GoJS will be notified that the data has changed
                    // and can update the node in the Diagram
                    // and record the change in the UndoManager
                    m.set(part.data, "BackgroundColour", this.data.Theme.RootNodeColour);

                }, "change root color");

            }
        }


        public destroyGraph() {
            this.div = null;
        }

        public getSelectedNode() {
            return this.filter.NodeKey;
        }

        public setHeight(height) {
            this.div.style.height = height;
            this.requestUpdate();
        }

        private showHideOverview() {

            if (this.nodes.count > 30) {
                $('#overviewDiv').show();
            } else {
                $('#overviewDiv').hide();
            }
        }

        private setTemplatesAndSettings(data: GraphDataResponse) {

            // setup node and edge templates
            this.buildNodeTemplate(data.Theme);
            this.buildLinkTemplate(data.Theme);
        }

        private setData(data: GraphDataResponse) {
            this.setDefaultNodeColors(data);
            this.data = Q.deepClone(data);
        }

        private setDefaultNodeColors(data: GraphDataResponse) {
            // set text color
            data.Nodes.forEach(element => {
                if (!element.TextColour) {
                    element.TextColour = data.Theme.NodeTextColour;
                }

                if (!element.BorderColour) {
                    element.BorderColour = data.Theme.NodeBorderColour;
                }
            });

            // set leaf default color
            data.Nodes.filter(x => !data.Edges.some(y => y[GraphEdgeRow.Fields.ParentNodeKey] === x[GraphNodeRow.Fields.NodeKey]))
                .forEach(element => {
                    if (!element.BackgroundColour) {
                        element.BackgroundColour = data.Theme.LeafNodeColour;
                    }
                });

            // set non-leaf default color
            data.Nodes.filter(x => data.Edges.some(y => y[GraphEdgeRow.Fields.ParentNodeKey] === x[GraphNodeRow.Fields.NodeKey])).forEach(element => {
                if (!element.BackgroundColour) {
                    element.BackgroundColour = data.Theme.NodeColour;
                }
            });
        }

        public refreshGraphData(nodes: GraphNodeRow[], edges: GraphEdgeRow[]) {
            this.startTransaction("refreshModel");
            this.animationManager.initialAnimationStyle = go.AnimationManager.AnimateLocations;

            let model = <go.GraphLinksModel>this.model;
            model.mergeNodeDataArray(nodes);
            model.mergeLinkDataArray(edges);

            this.commitTransaction("refreshModel");
        }

        private buildNodeTemplate(theme: GraphThemeRow) {

            let cx = this.$("ContextMenu");

            theme.DefaultContextMenuItems.forEach((elm: GraphContextMenuItem) => {
                cx.add(this.$(
                    "ContextMenuButton",
                    this.$(go.TextBlock, elm.Caption, { margin: 5 }),
                    {
                        click: (a: any, obj: any) => this.onContextMenuItemClick(a, obj)
                    }, { data: elm }
                ));
            });

            let nodeTemplate =
                this.$(
                    go.Node,
                    "Spot",
                    {
                        contextMenu: cx,  // end Adornment,
                        mouseEnter: (e: any, node: any) => this.onNodeMouseEnter(e, node),

                        toolTip:  // define a tooltip for each node that displays the color as text
                            this.$(
                                "ToolTip",
                                this.$(go.TextBlock, { margin: 4, stroke: "#696969" },
                                    new go.Binding("text", GraphNodeRow.Fields.Tooltip))
                            )  // end of Adornment
                    },
                    this.$(
                        "TreeExpanderButton",
                        { alignment: go.Spot.Bottom, alignmentFocus: go.Spot.Top },
                        { visible: true }
                    ),
                    this.$(
                        go.Panel,
                        "Auto",
                        {
                            // mouseEnter: this.onNodeMouseEnter,
                            mouseLeave: this.onNodeMouseLeave,
                            click: this.onNodeClick
                        },
                        this.$(
                            go.Shape,
                            { strokeWidth: 1, name: "nodeShape"},
                            new go.Binding("figure", GraphNodeRow.Fields.ShapeName),
                            new go.Binding("fill", GraphNodeRow.Fields.BackgroundColour),
                            new go.Binding("stroke", "isHighlighted", (h, n) => { return h ? this.data.Theme.HighlightColour : n.part.data[GraphNodeRow.Fields.BorderColour]; }).ofObject()
                        ),
                        this.$(
                            go.TextBlock,
                            { margin: 5, font: '10px Verdana', name: "nodeText" },
                            new go.Binding("stroke", GraphNodeRow.Fields.TextColour),
                            new go.Binding("isUnderline", GraphNodeRow.Fields.TextUnderline),
                            new go.Binding("textAlign", GraphNodeRow.Fields.TextAlign),
                            new go.Binding("text", GraphNodeRow.Fields.NodeKey, function (k) { return k; })
                        ),
                    ),
                );

            this.nodeTemplate = nodeTemplate;
        }

        private buildLinkTemplate(theme: GraphThemeRow) {

            let linkTemplate =
                this.$(
                    go.Link,
                    { selectable: false, fromEndSegmentLength: 5, toEndSegmentLength: 15 },

                    //this.$(
                    //    go.Shape,
                    //    { isPanelMain: true, stroke: "transparent", strokeWidth: 8 }
                    //),
                    this.$(
                        go.Shape,
                        { isPanelMain: true },
                        new go.Binding("stroke", "isHighlighted", (h, n) => { return h ? this.data.Theme.HighlightColour : n.part.data[GraphEdgeRow.Fields.Colour]; }).ofObject(),
                        new go.Binding("strokeWidth", GraphEdgeRow.Fields.Thickness)
                    ),
                    this.$(
                        go.Shape,
                        new go.Binding("fromArrow", GraphEdgeRow.Fields.FromArrow),
                        new go.Binding("fill", "isHighlighted", (h, n) => { return h ? this.data.Theme.HighlightColour : n.part.data[GraphEdgeRow.Fields.FromArrowColour]; }).ofObject()
                    ),
                    this.$(
                        go.Shape,
                        new go.Binding("toArrow", GraphEdgeRow.Fields.ToArrow),
                        new go.Binding("fill", "isHighlighted", (h, n) => { return h ? this.data.Theme.HighlightColour : n.part.data[GraphEdgeRow.Fields.ToArrowColour]; }).ofObject()
                    ),
                    {
                        toolTip:  // define a tooltip for each node that displays the color as text
                            this.$(
                                "ToolTip",
                                this.$(
                                    go.TextBlock,
                                    { margin: 4 },
                                    new go.Binding("text", GraphEdgeRow.Fields.Tooltip)
                                )
                            )  // end of Adornment
                    }
                );

            this.linkTemplate = linkTemplate;
        }

        private onContextMenuItemClick(a: any, obj: any) {

            // get the context menu that holds the button that was clicked
            let contextmenu = obj.part;

            // get the node data to which the Node is data bound
            let nodedata = contextmenu.data;

            let args: ContextMenuItemClickEventArgs = {
                Item: obj.data,
                Node: nodedata
            };

            switch (obj.data.Type) {
                case GraphContextMenuItemType.FocusNode:
                    this.onContextMenuFocusClick(args);
                    break;
                case GraphContextMenuItemType.WebUrl:
                    this.onContextMenuNavigateToUrlClick(args);
                    break;
                default:
                    console.warn("No event handler registered for the context menu type:" + obj.data.Type);
                    break;
            }
        }

        private onContextMenuFocusClick(args: ContextMenuItemClickEventArgs) {
            const nodeKey = args.Node[GraphNodeRow.Fields.NodeKey];
            this.filter.NodeKey = nodeKey;
            this.focusNode(this.filter);

            if (this.OnFocusChange) {
                this.OnFocusChange(args.Node);
            }
        }

        private onContextMenuNavigateToUrlClick(args: ContextMenuItemClickEventArgs) {
            let url = args.Item.Url.toLocaleLowerCase().replace('{key}', args.Node[GraphNodeRow.Fields.NodeKey]);
            window.open(url, '_blank');
        }

        private filterNodesRecursively(nodes: string[], nodeKey: string, up: boolean) {
            nodes.push(nodeKey);

            let links = up ?
                this.data.Edges.filter(x => x[GraphEdgeRow.Fields.ChildNodeKey] === nodeKey) :
                this.data.Edges.filter(x => x[GraphEdgeRow.Fields.ParentNodeKey] === nodeKey);
            if (!links) {
                return;
            }

            links.forEach(l => {
                const nextNode = up ? l[GraphEdgeRow.Fields.ParentNodeKey] : l[GraphEdgeRow.Fields.ChildNodeKey];
                if (nextNode) {
                    this.filterNodesRecursively(nodes, nextNode, up);
                }
            });
        }

        private onNodeMouseEnter(e: any, node: any) {

            let diagram = node.diagram;
            diagram.startTransaction("highlight");

            // remove any previous highlighting
            diagram.clearHighlighteds();

            this.paintPath(node, true);
            this.paintPath(node, false);

            diagram.commitTransaction("highlight");
        }

        private paintPath(node: any, up: boolean) {

            node.isHighlighted = true;

            let links = up ? node.findLinksInto() : node.findLinksOutOf();
            if (!links) {
                return;
            }

            links.each((l: any) => {
                l.isHighlighted = true;

                const nextNode = up ? l.fromNode : l.toNode;
                if (nextNode) {
                    this.paintPath(nextNode, up);
                }
            });
        }

        private onNodeMouseLeave(e: any, node: any) {
            e.diagram.commit((d: any) => d.clearHighlighteds(), "no highlighteds");
        }

        private onNodeClick(e: any, node: any) {

        }
    }

}